/**
 * # FlowPage
 * > a custom lifecycle for our pages
 * 
 * like simple flow but handle more stuff for page
 * - loading + app ready etc
 * - @TODO SEO metas
 * 
 */
import scrollStore from 'components/SmoothScroll/store';
import cursorStore from 'components/UiKit/Cursor/store';
import { client, getEnv, IS_DEV, motion, server } from 'config';
import { keys, map, merge, zipObject } from 'lodash';
import ApiHelper from 'plugins/api';
//  import storeLoader from 'components/ui/Loader/store';
import SEOStore from 'plugins/seo/store';
import appStore from 'plugins/store/app';
import { computed, ComputedRef, nextTick, onMounted, readonly, ref, Ref, unref, watch } from 'vue';



/**
  * STATES are incrmental values
  * *onLoaded will be triggered even if current state higher like READY*
  * (destroy states may be negative so super specific)
*/
export const STATES = {
  DEFAULT: 0,
  LOADED: 1,
  INIT: 2,
  INIT_UI: 3,
  READY: 4
}


export interface FlowPageOptions<T extends Obj> {
  data?: () => T
  api?: {
    url: string,
    format?: (data: any) => any,
    moc: boolean,
    getMoc: (a?: string) => Promise<AsyncImportMoc | any>,
  },
  cache?: Ref<O<string>>,
  /** load your data
   * will wait this loader, store data, then setState(LOADED)
   * > HOOK: ON_INIT avaible aftre this complete
  */
  load?: () => Promise<Obj>
  /** preload ui dependencies + wait onMounted
   * triggered only if `motion`- start loading on setup but do not bloack maiin loading state
   * > HOOK: ON_INIT_UI avaible after completion
   * ui:{
   *   gsap: ()=>import('gsap').then(m=>m.default)
   * }import { unref } from 'vue';
  */
  ui?: {
    [key: string]: () => Promise<any>
  }
  /**
   * **MUST BE CALLED FOR PAGES** -  used to hide loader etc
   * 
   * You may let this flow handle `autoSetReady`
   * or call yourself `const {setReady} = useFlow()`
   * - if motion, please initUi() and hide stuff before setReady()
   * - else, call setReady right after onInit()
  */
  autoSetReady?: boolean
  /** if true, setReady trigger appStore.setPageReady() */
  isPage?: boolean
  /** if true, log flow etc */
  debug?: boolean | string
}

export type FlowHookArgs = {
  client: boolean, server: boolean, motion: boolean
}
// type FlowCallback = (arg0:{
//   client:boolean, server:boolean, motion:boolean
// })=>unknown

export interface FlowPage<T> {
  /** 
   * ready to render CPN
   * - actualy event before STATES.READY (when ui hidden)
   * - true when INIT/INIT_UI
  */
  ready: ComputedRef<boolean>
  loaded: ComputedRef<boolean>
  readyToRender: ComputedRef<boolean>
  state: Ref<number>
  /** return api data - if response = {meta, data} then getData will return only the `data` node (without seo) */
  getData: () => Ref<T>
  /** return api data - if response = {meta, data} then getMeta will return only the `meta` node */
  getMeta: () => Obj
  /** return api data.seo - just because used only in html metas... */
  getSEO: () => Obj
  /** return api data.translations - for locale switcher + seo alternate */
  getTranslation: () => Obj
  /** once data ready and correct lifecicle hook - may call auto.
   * > exept if declare autoSetReady=true **you must setReady yourself**
  */
  setReady: () => void,
  /** just after loading stuff - before onInit */
  onloaded: (next: (arg: FlowHookArgs & { data: T }) => unknown) => Promise<unknown>
  /**
   * data loaded - like a onMounted()
   * - if server = right after loaded
   * - if client = loaded + mounted
  */
  onInit: (next: (arg: FlowHookArgs & { data: T }) => unknown) => Promise<unknown>
  /**
   * init ui stuff (init + preload ui + moUnted)
   * **only if motion**
  */
  onInitUi: (next: (arg: FlowHookArgs & { data: T } & { ui: Obj }) => unknown) => Promise<unknown>
}


export function usePageFlow<T extends Obj>(opts: FlowPageOptions<T> = {}): FlowPage<T> {
  const options = {
    autoSetReady: true,
    ...opts
  }
  function debug(...params: any[]) { // eslint-disable-line
    if (!options.debug || !IS_DEV) return
  }

  const state = ref<number>(STATES.DEFAULT)
  let uiVendors = {}

  const _data = ref((options.data?.() ?? {}) as T) // store the response `meta` node (like no meta or data.data)
  function getData() {
    return readonly(_data)
    // const ensured:unknown = readonly(_data) 
    // return ensured as Ref<T>
  }

  const _meta = ref<Obj>({}) // store the response `meta` node
  function getMeta() {
    return readonly(_meta.value) as Obj
  }

  // @todo define Obj type
  const _seo = ref<Obj>({}) // store the response `seo` node
  function getSEO() {
    return readonly(_seo?.value || undefined) as Obj
  }

  // @todo define Obj type
  const _translation = ref<any>() // store the response `translation` node
  function getTranslation() {
    return readonly(_translation?.value || undefined)
  }

  /**
   * to be triggered once loading ready
   * May wait some required states before
   */
  function setReady(ready = true) {
    if (!ready) {
      state.value = STATES.DEFAULT
      return
    }
    if (state.value === STATES.READY) {
      setTimeout(() => {
        // trigger SEO update from data received
        SEOStore.UPDATE(getSEO(), getTranslation());

        appStore.setPageReady()
        // storeLoader.setLoaded() // nop
        scrollStore.refreshScroll();

        setTimeout(() => {
          window.prerenderReady = true;
        }, 500);
      }, 500)
      return
    }
    const requiredState = motion ? STATES.INIT_UI : STATES.INIT
    debug('setReady -', { motion, state: state.value, requiredState });
    waitForState(requiredState)
      .then(() => {
        // trigger SEO update from data received
        SEOStore.UPDATE(getSEO(), getTranslation());

        state.value = STATES.READY
        appStore.setPageReady()
        // storeLoader.setLoaded() // nop
        scrollStore.refreshScroll()

        setTimeout(() => {
          window.prerenderReady = true;
        }, 500);
      })
  }

  function waitForState(stateToMatch: number) {
    return new Promise(resolve => {
      const unwatch = watch(state, (current) => {
        debug('waitForState', current, stateToMatch);
        if (current >= stateToMatch) {
          setTimeout(() => {
            resolve(true)
            unwatch()
          }, 14) // this setTimeout allow to setup all sub components before (if they use useFlow)
        }
      }, { immediate: true, flush: 'post' })
    })
  }


  async function load() {
    return new Promise(resolve => {
      if (typeof options.load === 'function')
        return resolve(options.load())

      if (window.__PAGE && !appStore.firstPageReady) {
        return resolve(options.api?.format?.({ data: window.__PAGE }) ?? window.__PAGE)
      }

      if (options.cache?.value && options.cache?.value !== 'undefined') {
        const cache = options.cache.value
        return resolve(JSON.parse(cache))
      }

      if (options.api) {
        const useMoc = (options.api.moc || getEnv('VITE_MOC_API')) && options.api.getMoc
          ? options.api.getMoc
          : undefined

        return resolve(ApiHelper.get(
          options.api.url,
          options.api,
          useMoc
        )
          .then((response) => {
            if (useMoc) return response
            return options.api?.format?.(response) ?? response
          })
          .catch(error => {
            console.error(error)
            throw error
          }))
      }

      return resolve({})
    })
      .then((response?: any) => {
        function mergeWithOptionsData(data: any) {
          if (!options.data) return data
          return merge({}, options.data(), data ?? {})
        }
        if (typeof response === 'object' && response) {
          if (response.data) {
            if (response.meta) _meta.value = response.meta
            const { seo, translations, translation, ...page } = response.data
            if (seo) _seo.value = seo
            if (translations || translation) _translation.value = translations || translation
            _data.value = mergeWithOptionsData(page)
          } else {
            const { seo, translations, translation } = response
            if (seo) _seo.value = seo
            if (translations || translation) _translation.value = translations || translation
            _data.value = mergeWithOptionsData(response)
          }
        }

        state.value = STATES.LOADED
        if (options.cache?.value) {
          options.cache.value = JSON.stringify(response)
        }
        return response
      })
      // on error, with page flow sometimes this is reallll bad
      .catch(() => {
        // set as 404
        appStore.setIs404();
      })
  }

  async function preloadUi() {
    return new Promise(resolve => {
      if (options.ui) {
        return Promise.all(map(options.ui, i => i()))
          .then((args) => zipObject(keys(options.ui), args))
          .then(resolve)
      }
      return resolve(undefined)
    })
      .then(payload => {
        if (payload && typeof payload === 'object') {
          uiVendors = payload // no reactive shits here
        }
        return payload
      })
  }


  Promise.all([
    load(),
    new Promise(resolve => {
      if (server) return resolve(true)
      onMounted(() => resolve(true))
    })
  ])
    .then((data) => {
      nextTick(() => { state.value = STATES.INIT })
      return data
    })

  if (motion) {
    Promise.all([
      preloadUi(),
      waitForState(STATES.INIT),
      new Promise(resolve => onMounted(() => resolve(true)))
    ]).then((ui) => {

      nextTick(() => {
        state.value = STATES.INIT_UI

        nextTick(() => {
          cursorStore.updateCursor();
          scrollStore.refreshScroll();
          setTimeout(scrollStore.updateScroll, 500)
        })
      })
      return ui
    })
  }
  // debug('[] autoSetReady?', options.autoSetReady);
  if (options.autoSetReady) setReady()

  return {
    loaded: computed(() => state.value >= STATES.LOADED),
    ready: computed(() => state.value >= STATES.READY),
    readyToRender: computed(() => state.value >= (motion ? STATES.INIT_UI : STATES.INIT)),
    getData,
    getMeta,
    getSEO,
    getTranslation,
    setReady,
    waitForState,
    state,
    /** just after loading stuff - before onInit */
    onloaded: async function (next: (arg: FlowHookArgs & { data: T }) => unknown) {
      await waitForState(STATES.INIT)
      return next({ client, server, motion, data: (unref(_data) as T) })
    },
    /** data loaded - like a onMounted() */
    onInit: async function (next: (arg: FlowHookArgs & { data: T }) => unknown) {
      await waitForState(STATES.INIT)
      return next({ client, server, motion, data: (unref(_data) as T) })
    },
    onInitUi: async function (next: (arg: FlowHookArgs & { data: T } & { ui: Obj }) => unknown) {
      if (!motion) return
      await waitForState(STATES.INIT_UI)

      return next({ client, server, motion, data: (unref(_data) as T), ui: uiVendors })
    },

  } as FlowPage<T>
}

export default usePageFlow
